// GlimtApp — Snap-lignende mobil-inngang for Glimt.
// Én liste per venn: farge-firkant = ny fra dem (rød=glimt, blå=DM),
// pil på høyre = din sending (fylt=uåpnet, omriss=åpnet).
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Camera, Search, UserPlus, X, Play, Trash2, Plus, Eye } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { isMobileDevice } from "../lib/isMobileDevice";
import GlimtCamera from "../components/GlimtCamera";
import GlimtEditor from "../components/GlimtEditor";
import Layout from "../components/Layout";
import { toast } from "sonner";

const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Norsk relativ tid: "nå", "5m", "2t", "3d", "1u".
 * Kompakt format for tett Snap-lignende liste.
 */
function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "nå";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}t`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}u`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}md`;
  return `${Math.floor(d / 365)}å`;
}

/** Status-tekst under navnet, med farge basert på incoming/outgoing state. */
function statusFor(f) {
  const inc = f.incoming;
  const imt = f.incoming_media_type;
  const out = f.outgoing;
  const outType = f.outgoing_type;
  const rel = formatRelative(f.last_activity);
  if (inc === "new_glimt") return { text: `Nytt Glimt${rel ? ` · ${rel}` : ""}`, color: "text-red-400" };
  if (inc === "new_dm" && imt === "media") return { text: `Sendte bilde${rel ? ` · ${rel}` : ""}`, color: "text-red-400" };
  if (inc === "new_dm") return { text: `Sendte melding${rel ? ` · ${rel}` : ""}`, color: "text-blue-400" };
  if (out === "sent_unopened") {
    const label = outType === "media" ? "Sendte bilde" : "Sendte melding";
    return { text: `▶ ${label}${rel ? ` · ${rel}` : ""}`, color: outType === "media" ? "text-red-400" : "text-blue-400" };
  }
  if (out === "sent_opened") {
    return { text: `▷ Åpnet${rel ? ` · ${rel}` : ""}`, color: "text-white/40" };
  }
  return { text: "", color: "" };
}

export default function GlimtApp() {
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [myStories, setMyStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [capture, setCapture] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [openMsg, setOpenMsg] = useState(null);
  const [composeFor, setComposeFor] = useState(null);
  const [viewsGid, setViewsGid] = useState(null);
  const [mobile, setMobile] = useState(true);

  useEffect(() => { setMobile(isMobileDevice()); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [rf, rs] = await Promise.all([
        api.get("/me/glimt-friends"),
        api.get("/me/glimt").catch(() => ({ data: { items: [] } })),
      ]);
      setFriends(rf.data.items || []);
      const now = Date.now();
      const active = (rs.data.items || []).filter((g) => {
        if (g.archived) return false;
        if (g.scheduled_at) return false;
        if (!g.published_at) return false;
        if (g.expires_at) {
          const exp = new Date(g.expires_at).getTime();
          if (!Number.isNaN(exp) && exp <= now) return false;
        }
        return true;
      });
      // Sist publisert først
      active.sort((a, b) => (b.published_at || "").localeCompare(a.published_at || ""));
      setMyStories(active);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (!mobile) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-6 space-y-4" data-testid="glimt-app-desktop-notice">
          <h1 className="text-2xl font-editorial text-[var(--magenta)]">Glimt</h1>
          <p className="text-[var(--cream-soft)]">
            Glimt-flyten er kun på mobil. Åpne norskerotikk.no på telefonen for kamera og innboks.
          </p>
          <p className="text-[var(--cream-faint)] text-xs">
            For å organisere og planlegge innhold, gå via Skaperstudio → Glimt-studio.
          </p>
        </div>
      </Layout>
    );
  }

  const handleRowClick = async (f) => {
    if (f.incoming === "new_glimt") {
      navigate(`/glimt/${f.creator.username}`);
    } else if (f.incoming === "new_dm") {
      // Åpne siste ulest DM fra dem via thread-endpoint
      try {
        const r = await api.get(`/me/glimt-dm/thread/${f.creator.id}`);
        const msgs = r.data.messages || [];
        // Finn siste ulest fra dem
        const last = [...msgs].reverse().find((m) => m.recipient_id !== f.creator.id && !m.opened_at);
        if (last) {
          try { await api.post(`/me/glimt-dm/${last.id}/open`); } catch { /* stille */ }
          setOpenMsg(last);
          setTimeout(load, 500);
        } else {
          setComposeFor(f.creator);
        }
      } catch (e) { toast.error(formatApiError(e)); }
    } else {
      // Ingen ny fra dem → åpne composer for ny DM
      setComposeFor(f.creator);
    }
  };

  return (
    <Layout>
      <div className="min-h-[calc(100dvh-8rem)] bg-[var(--noir)] -mx-3 -my-3 flex flex-col" data-testid="glimt-app">
        {/* Header */}
        <div className="px-3 pt-3 pb-2 flex items-center gap-2">
          <h1 className="font-fraunces italic text-2xl text-[var(--cream)] flex-1">Glimt</h1>
          <button
            onClick={() => setAddOpen(true)}
            className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-[var(--cream)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
            data-testid="glimt-add-friend-btn"
            aria-label="Legg til venn"
          >
            <UserPlus className="w-5 h-5" />
          </button>
        </div>

        {/* Mine stories — Snap-style horisontal strip */}
        <MyStoriesStrip
          stories={myStories}
          onAdd={() => setCamOpen(true)}
          onOpenViews={(gid) => setViewsGid(gid)}
        />

        {/* Vennelisten */}
        <div className="flex-1 overflow-y-auto px-3 pb-24">
          {loading ? (
            <div className="text-[var(--cream-faint)] text-sm text-center py-10">Laster…</div>
          ) : friends.length === 0 ? (
            <EmptyState onAdd={() => setAddOpen(true)} />
          ) : (
            <div className="space-y-1">
              {friends.map((f) => (
                <FriendRow key={f.creator.id} f={f} onClick={() => handleRowClick(f)} />
              ))}
            </div>
          )}
        </div>

        {/* Flytende kamera-knapp */}
        <button
          onClick={() => setCamOpen(true)}
          className="fixed bottom-24 right-6 w-16 h-16 rounded-full bg-[var(--magenta)] shadow-2xl flex items-center justify-center z-30 hover:scale-105 transition-transform"
          data-testid="glimt-camera-fab"
          aria-label="Nytt Glimt"
        >
          <Camera className="w-7 h-7 text-black" />
        </button>

        {/* Kamera */}
        {camOpen && !capture && (
          <GlimtCamera onClose={() => setCamOpen(false)} onCapture={(c) => setCapture(c)} />
        )}
        {capture && (
          <GlimtEditor
            capture={capture}
            onClose={() => { setCapture(null); setCamOpen(false); }}
            onPublished={() => { setCapture(null); setCamOpen(false); load(); }}
          />
        )}

        {/* Legg til venn */}
        {addOpen && (
          <AddFriendModal onClose={() => { setAddOpen(false); load(); }} />
        )}

        {/* DM-viewer */}
        {openMsg && (
          <DMViewer msg={openMsg} onClose={() => { setOpenMsg(null); load(); }} />
        )}

        {/* Composer */}
        {composeFor && (
          <DMComposer creator={composeFor} onClose={() => { setComposeFor(null); load(); }} />
        )}

        {/* Views-modal for egen story */}
        {viewsGid && (
          <StoryViewsModal
            gid={viewsGid}
            onClose={() => setViewsGid(null)}
            onDeleted={() => { setViewsGid(null); load(); }}
          />
        )}
      </div>
    </Layout>
  );
}

function FriendRow({ f, onClick }) {
  const incoming = f.incoming; // "new_glimt" | "new_dm" | "none"
  const incomingMediaType = f.incoming_media_type; // "text" | "media" | null (kun for new_dm)
  const outgoing = f.outgoing; // "sent_unopened" | "sent_opened" | "none"
  const outType = f.outgoing_type; // "text" | "media" | null

  // Firkant venstre — RØD for glimt/media-DM, BLÅ for tekst-DM
  let squareClass = "border-2 border-white/60";
  if (incoming === "new_glimt") {
    squareClass = "bg-red-500 border-2 border-red-500";
  } else if (incoming === "new_dm") {
    squareClass = incomingMediaType === "media"
      ? "bg-red-500 border-2 border-red-500"
      : "bg-blue-500 border-2 border-blue-500";
  }

  // Pil høyre
  let arrow = null;
  if (outgoing !== "none") {
    const filled = outgoing === "sent_unopened";
    const color = outType === "media" ? "text-red-500" : "text-blue-500";
    const fillColor = outType === "media" ? "fill-red-500" : "fill-blue-500";
    arrow = (
      <svg
        viewBox="0 0 24 24"
        className={`w-4 h-4 ${color} ${filled ? fillColor : "fill-none"}`}
        strokeWidth={filled ? 0 : 2}
        stroke="currentColor"
        data-testid={`glimt-arrow-${outgoing}`}
      >
        <path d="M4 12 L14 4 L14 8 L20 8 L20 16 L14 16 L14 20 Z" />
      </svg>
    );
  }

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded hover:bg-white/5 text-left"
      data-testid={`glimt-friend-row-${f.creator.username}`}
    >
      <div className={`w-4 h-4 shrink-0 ${squareClass}`} data-testid="glimt-row-square" />

      <div className="w-11 h-11 shrink-0 rounded-full bg-[var(--noir-elev)] flex items-center justify-center overflow-hidden">
        {f.creator.avatar_url ? (
          <img src={f.creator.avatar_url} className="w-full h-full object-cover" alt="" />
        ) : (
          <span className="text-sm font-bold text-[var(--cream)]">
            {(f.creator.username || "?")[0]?.toUpperCase()}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-meta text-[15px] text-[var(--cream)] truncate">
          {f.creator.creator_handle || `@${f.creator.username}`}
        </div>
        {(() => {
          const s = statusFor(f);
          return s.text ? (
            <div className={`text-xs mt-0.5 truncate ${s.color}`} data-testid="glimt-row-status">
              {s.text}
            </div>
          ) : null;
        })()}
      </div>

      {arrow && <div className="shrink-0">{arrow}</div>}
    </button>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="text-center py-16 space-y-4" data-testid="glimt-empty-state">
      <div className="text-[var(--cream-faint)] text-sm">
        Du har ingen Glimt-venner ennå.
      </div>
      <button
        onClick={onAdd}
        className="btn-magenta inline-flex items-center gap-2"
        data-testid="glimt-empty-add-btn"
      >
        <UserPlus className="w-4 h-4" /> Legg til venn
      </button>
    </div>
  );
}

function AddFriendModal({ onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 1) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/glimt/search-creators?q=${encodeURIComponent(q.trim())}`);
        setResults(r.data.items || []);
      } catch { /* stille */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const add = async (c) => {
    setBusy(true);
    try {
      await api.post(`/glimt/friends/${c.id}`);
      toast.success(`La til ${c.username}`);
      setResults((rs) => rs.map((x) => x.id === c.id ? { ...x, is_friend: true } : x));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--noir)] flex flex-col" data-testid="glimt-add-modal">
      <div className="flex items-center gap-2 p-3 border-b border-white/10">
        <button onClick={onClose} className="w-9 h-9 flex items-center justify-center" data-testid="glimt-add-close">
          <X className="w-5 h-5 text-[var(--cream)]" />
        </button>
        <h2 className="font-fraunces italic text-xl text-[var(--cream)]">Legg til venn</h2>
      </div>
      <div className="p-3">
        <div className="relative">
          <Search className="w-4 h-4 text-[var(--cream-faint)] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Søk brukernavn eller handle"
            className="input-editorial w-full !pl-10"
            autoFocus
            data-testid="glimt-add-search-input"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-6">
        {q.trim().length === 0 ? (
          <div className="text-center py-10 text-[var(--cream-faint)] text-sm">
            Skriv for å søke etter skapere som har Glimt.
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-10 text-[var(--cream-faint)] text-sm">
            Ingen treff.
          </div>
        ) : (
          <div className="space-y-1">
            {results.map((c) => (
              <div key={c.id} className="flex items-center gap-3 p-3 rounded bg-white/5" data-testid={`glimt-add-result-${c.username}`}>
                <div className="w-10 h-10 rounded-full bg-[var(--noir-elev)] flex items-center justify-center overflow-hidden">
                  {c.avatar_url ? (
                    <img src={c.avatar_url} className="w-full h-full object-cover" alt="" />
                  ) : (
                    <span className="text-sm font-bold text-[var(--cream)]">{(c.username || "?")[0]?.toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--cream)] truncate">{c.creator_handle || `@${c.username}`}</div>
                </div>
                {c.is_friend ? (
                  <span className="text-xs text-[var(--cream-faint)]" data-testid={`glimt-add-status-friend-${c.username}`}>
                    ✓ Venn
                  </span>
                ) : (
                  <button
                    onClick={() => add(c)}
                    disabled={busy}
                    className="btn-magenta text-xs px-3 py-1.5"
                    data-testid={`glimt-add-btn-${c.username}`}
                  >
                    Legg til
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DMViewer({ msg, onClose }) {
  const [remaining, setRemaining] = useState(15);
  const isEphemeralNow = msg.ephemeral === "on_open";
  useEffect(() => {
    if (!isEphemeralNow) return;
    let n = 15;
    setRemaining(n);
    const t = setInterval(() => {
      n -= 1; setRemaining(n);
      if (n <= 0) { clearInterval(t); onClose?.(); }
    }, 1000);
    return () => clearInterval(t);
  }, [isEphemeralNow, onClose]);

  const mediaSrc = msg.storage_path ? `/api/glimt-dm/${msg.id}/media` : null;
  const del = async () => {
    try { await api.delete(`/me/glimt-dm/${msg.id}`); toast.success("Slettet"); onClose?.(); } catch { /* stille */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black" data-testid="glimt-dm-viewer">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white z-10"
        data-testid="glimt-dm-close"
      >
        <X className="w-5 h-5" />
      </button>
      {isEphemeralNow && (
        <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-red-600/80 text-white text-xs font-bold z-10">
          Slettes om {remaining}s
        </div>
      )}
      <div className="absolute inset-0 flex items-center justify-center">
        {msg.media_type === "image" && mediaSrc && (
          <img src={mediaSrc} alt="" className="max-w-full max-h-full object-contain" />
        )}
        {msg.media_type === "video" && mediaSrc && (
          <video src={mediaSrc} className="max-w-full max-h-full" autoPlay playsInline controls />
        )}
        {(msg.media_type === "text" || (!msg.media_type)) && (
          <div className="text-white text-xl px-8 text-center whitespace-pre-wrap">
            {msg.message}
          </div>
        )}
      </div>
      <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-3 z-10">
        <button
          onClick={del}
          className="px-4 py-2 rounded-full bg-black/60 text-white text-xs flex items-center gap-2"
          data-testid="glimt-dm-delete"
        >
          <Trash2 className="w-3.5 h-3.5" /> Slett
        </button>
      </div>
    </div>
  );
}

function DMComposer({ creator, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("recipient_username", creator.username);
      fd.append("message", text.trim());
      await api.post("/glimt-dm/send", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sendt");
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--noir)] flex flex-col" data-testid="glimt-composer">
      <div className="flex items-center gap-2 p-3 border-b border-white/10">
        <button onClick={onClose} className="w-9 h-9 flex items-center justify-center" data-testid="glimt-composer-close">
          <X className="w-5 h-5 text-[var(--cream)]" />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-full bg-[var(--noir-elev)] flex items-center justify-center overflow-hidden">
            {creator.avatar_url ? (
              <img src={creator.avatar_url} className="w-full h-full object-cover" alt="" />
            ) : (
              <span className="text-sm font-bold text-[var(--cream)]">{(creator.username || "?")[0]?.toUpperCase()}</span>
            )}
          </div>
          <div className="font-meta text-sm text-[var(--cream)] truncate">
            {creator.creator_handle || `@${creator.username}`}
          </div>
        </div>
      </div>
      <div className="p-3 flex-1">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Skriv en melding…"
          className="input-editorial w-full h-40"
          autoFocus
          maxLength={1000}
          data-testid="glimt-composer-text"
        />
        <div className="text-xs text-[var(--cream-faint)] mt-1">{text.length}/1000</div>
      </div>
      <div className="p-3 border-t border-white/10">
        <button
          onClick={send}
          disabled={busy || !text.trim()}
          className="btn-magenta w-full py-3 flex items-center justify-center gap-2"
          data-testid="glimt-composer-send"
        >
          <Play className="w-4 h-4" /> {busy ? "Sender…" : "Send"}
        </button>
      </div>
    </div>
  );
}


/**
 * Mine stories — Snap-style horisontal strip øverst i Glimt-appen.
 * Første boble: "+ Ny story" åpner kamera (som også har galleri-picker).
 * Deretter én boble per aktiv story-glimt med thumbnail + view-count.
 */
function MyStoriesStrip({ stories, onAdd, onOpenViews }) {
  return (
    <div className="px-3 pb-3 border-b border-white/5" data-testid="glimt-my-stories-strip">
      <div className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--cream-faint)] mb-2">
        Min story
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
        {/* + Ny story */}
        <button
          onClick={onAdd}
          className="shrink-0 flex flex-col items-center gap-1"
          data-testid="glimt-my-story-add"
        >
          <div className="w-16 h-20 border-2 border-dashed border-[var(--magenta)]/60 flex items-center justify-center bg-[var(--noir-elev)]">
            <Plus className="w-6 h-6 text-[var(--magenta)]" />
          </div>
          <span className="text-[10px] text-[var(--cream-soft)]">Ny story</span>
        </button>

        {stories.map((g) => (
          <button
            key={g.id}
            onClick={() => onOpenViews(g.id)}
            className="shrink-0 flex flex-col items-center gap-1"
            data-testid={`glimt-my-story-${g.id}`}
          >
            <div className="relative w-16 h-20 border border-[var(--magenta)]/50 overflow-hidden bg-black">
              {g.media_type === "video" ? (
                <video
                  src={`${MEDIA_BASE}/api/glimt/${g.id}/media`}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  src={`${MEDIA_BASE}/api/glimt/${g.id}/media`}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5 flex items-center justify-center gap-1">
                <Eye className="w-3 h-3 text-[var(--cream)]" />
                <span className="text-[10px] text-[var(--cream)] font-meta">
                  {g.view_count || 0}
                </span>
              </div>
            </div>
            <span className="text-[10px] text-[var(--cream-soft)]">
              {formatRelative(g.published_at)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Story-views-modal — samme innhold som GlimtStudio::ViewsModal (v109).
 * Viser hvem som har sett + reaksjoner + slett-knapp.
 */
function StoryViewsModal({ gid, onClose, onDeleted }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/me/glimt/${gid}/views`).then((r) => setData(r.data)).catch(() => {});
  }, [gid]);

  const del = async () => {
    if (!window.confirm("Slett denne storien? Kan ikke angres.")) return;
    setBusy(true);
    try {
      await api.delete(`/me/glimt/${gid}`);
      toast.success("Story slettet");
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="glimt-my-story-views-modal"
    >
      <div
        className="bg-[var(--noir-elev)] border border-[var(--magenta)]/30 max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 border-b border-white/10">
          <h3 className="font-fraunces italic text-lg text-[var(--cream)]">Visninger & reaksjoner</h3>
          <button onClick={onClose} data-testid="glimt-my-story-views-close">
            <X className="h-5 w-5 text-[var(--cream-soft)]" />
          </button>
        </div>
        {data && (
          <div className="p-4 space-y-4">
            <p className="text-sm text-[var(--cream-soft)]">
              Totalt: {data.total_views} visninger, {(data.reactions || []).length} reaksjoner
            </p>

            {(data.views || []).length > 0 && (
              <div>
                <div className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--cream-faint)] mb-2">
                  Hvem har sett ({(data.views || []).length})
                </div>
                <div className="divide-y divide-white/5" data-testid="glimt-my-story-views-list">
                  {(data.views || []).map((v) => (
                    <Link
                      key={v.user_id}
                      to={v.username ? `/profile/${v.username}` : "#"}
                      className="flex items-center gap-3 py-2 hover:bg-white/5 px-1 transition-colors"
                    >
                      <div className="h-8 w-8 rounded-sm bg-[var(--magenta)]/20 flex items-center justify-center overflow-hidden shrink-0">
                        {v.avatar_url ? (
                          <img src={v.avatar_url} alt={v.username || ""} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-[10px] font-bold text-[var(--magenta)]">
                            {(v.username || "?").slice(0, 2).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[var(--cream)] truncate">
                          @{v.username || "ukjent"}
                        </div>
                        <div className="text-[10px] text-[var(--cream-faint)]">
                          {formatRelative(v.viewed_at)}
                          {v.view_count > 1 && ` · sett ${v.view_count} ganger`}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {(data.reactions || []).length > 0 && (
              <div>
                <div className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--cream-faint)] mb-2">
                  Reaksjoner ({(data.reactions || []).length})
                </div>
                <div className="space-y-2">
                  {(data.reactions || []).map((r) => (
                    <div key={r.id} className="border border-white/10 p-2 text-xs text-[var(--cream)]">
                      <div className="flex items-center gap-2 mb-1">
                        {r.username && (
                          <Link to={`/profile/${r.username}`} className="text-[var(--magenta)] hover:underline">
                            @{r.username}
                          </Link>
                        )}
                        <span className="text-[10px] text-[var(--cream-faint)]">
                          {formatRelative(r.created_at)}
                        </span>
                      </div>
                      {r.emoji && <span className="text-lg mr-2">{r.emoji}</span>}
                      {r.message && <span className="italic">{r.message}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-2 border-t border-white/10">
              <button
                onClick={del}
                disabled={busy}
                className="w-full py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs font-meta tracking-[0.15em] flex items-center justify-center gap-2 transition"
                data-testid="glimt-my-story-delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {busy ? "SLETTER…" : "SLETT STORY"}
              </button>
            </div>
          </div>
        )}
        {!data && (
          <div className="p-8 text-center text-[var(--cream-faint)] text-sm">Laster…</div>
        )}
      </div>
    </div>
  );
}
